import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, lazy, Suspense } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { useSelector } from 'react-redux'
import { useAppSelector, useAppDispatch } from './hooks'
import { useBeforeUnload } from './hooks/useBeforeUnload'
import { isPhaserGameReady, getPhaserGameSync, initializePhaserGame } from './PhaserGame'
import './theme/sevvTheme.scss'
import {
  setLoggedIn,
  setConnectionStatus,
  ConnectionStatus,
  setAuthenticated,
  setUserData,
  setPlayerName,
} from './stores/UserStore'
import store, { RootState } from './stores'
import { setLoadingGame } from './stores/GameStore'
import AppController from './core/controllers/AppController'
// OPTIMIZATION: Lazy load controllers and dialogs - only load when needed
const AdminController = lazy(() => import('./controllers/AdminController'))
const GameMenuController = lazy(() => import('./controllers/GameMenuController'))
import HelperController from './controllers/HelperController'
import { addNotification } from './stores/NotificationStore'
import { setMyTodaysBookings } from './stores/RoomBookingStore'
import { selectOnboardingHoldsApp } from './stores/OnboardingStore'
import { AuthMessages, googleCallbackErrorEntry } from './components/AuthenticationDialog.messages'
// OPTIMIZATION: Lazy load ComputerDialog - only loads when dialog is opened
const ComputerDialog = lazy(() => import('./components/ComputerDialog'))
// NameplateList removed — nameplates now render inside phaser-react-ui GameOverlayUI
import { disableCameraRepositioning } from './utils/ProximityDisableTest';
import CircularProgress from '@mui/material/CircularProgress'
import OptimizedLoadingScreen from './components/OptimizedLoadingScreen'
import SafeSubtree from './components/SafeSubtree'
import { CssBaseline } from '@mui/material'
// react-hot-toast Toaster removed — unused; app uses Redux Notifications and MUI Snackbar instead
import RequiredProfileCheck from './components/RequiredProfileCheck'
// OPTIMIZATION: Lazy load LiveKit component (~1MB) - only loads when video/audio is needed
const LiveKitRoomConnector = lazy(() => import('./components/LiveKitRoomConnector'))
import { LiveKitCallControlsProvider } from './components/LiveKitVideoDisplay/context/LiveKitCallControlsContext'
// Import the constant separately (lightweight)
export const LIVEKIT_AUTO_CONNECT_AFTER_LOADING = 'livekit-auto-connect-after-loading'
// Pluggable chat panel (Matrix or Parse backend, chosen per company via
// Company.matrix_chat_enabled) — replaces the Parse ChatInterface once enabled.
const MatrixChatMount = lazy(() => import('./components/ChatPanel/MatrixChatMount'))
import { phaserEvents } from './events/EventCenter'
import GoogleOAuthCallback from './components/GoogleOAuthCallback'
import PasswordResetPage from './components/PasswordResetPage'
import EmailVerificationPage from './components/EmailVerificationPage'
import AcceptInvitationPage from './components/AcceptInvitationPage'
import JoinCompanyPage from './components/JoinCompanyPage'
import { JOIN_COMPANY_PATH } from './services/CompanyInviteLinkService'
import GuestJoinPage from './components/GuestJoinPage'
import { guestJoinFailureUrl } from './utils/guestFarewellUrl'
import GuestFarewellScreen from './components/RoomBooking/GuestFarewellScreen'
import InvitationEmailPlaceholderPage from './components/InvitationEmailPlaceholderPage'
import authService from './services/authService'
import { UserRole } from './types/user'
import { coerceUserProfile, readParseGoogleLinked, userProfileToFullUserData } from './account'
import Parse from 'parse'
import { parseCloudRun } from './services/parseCloud'
import {
  clearGoogleOAuthHandoffStorage,
  GOOGLE_OAUTH_LOCAL_REDIRECT_KEY,
  GOOGLE_OAUTH_LOCAL_STATE_KEY,
  resolveGoogleOAuthRedirectUri,
  validateGoogleOAuthReturnState,
} from './utils/googleOAuthClient'
import HelpTooltip from './components/HelpTooltip'
import FeedbackDrawer from './components/FeedbackDrawer'
import ProductUpdatesDialogHost from './components/ProductUpdates/ProductUpdatesDialogHost'
import TermsGateProvider, { isGuestUserId, TermsGated } from './components/consent/TermsGate'
import ConnectionUnstableToast from './components/ConnectionUnstableToast'
import Notifications from './components/Notifications'
import RichNotificationToast from './components/RichNotificationToast'
import StatusBannerViewport from './components/StatusBannerViewport'
import ZoomedControlsIndicator from './components/ZoomedControlsIndicator'
import keyboardHandler from './services/KeyboardHandler'
import { setupRoutingListeners, initializeRouting } from './stores/routingStore'
import AppErrorBoundary from './components/AppErrorBoundary'
import { useAdminSessionCapabilities } from './hooks/useAdminSessionCapabilities'
import { useCompanyPermissions } from './hooks/useCompanyPermissions'
import { normalizeRoutePath } from './utils/routeUtils'
import { useElevatedAdmin } from './hooks/usePermission'
import { useLatchedValue } from './hooks/useLatchedValue'
import { routingUtils } from './utils/routingUtils'
import {
  shouldMountChatPanel,
  shouldMountInRoomAdminController,
  shouldMountInRoomBottomBar,
  shouldMountPreLoadAdminController,
} from './utils/appRenderGates'
import EscortGuestPanel from './components/RoomBooking/EscortGuestPanel'
import { useReceptionGuests } from './components/RoomBooking/useReceptionGuests'
import { getDisplayName } from './utils/personName'


// Add global style to prevent body overflow
const GlobalStyle = createGlobalStyle`
  html, body {
    overflow: hidden;
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    background-color: var(--sevv-bg-default, #fafafa);
  }

  /* Stabilize the canvas to prevent layout shifts */
  canvas {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    will-change: transform;
  }

  /* Prevent layout shifts during animations */
  * {
    box-sizing: border-box;
  }
`

const Backdrop = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
`

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 999;
`

// TODO(matrix-chat, pre-go-live): drop FORCE_MATRIX_CHAT_COMPANY_IDS and gate purely on
// currentCompany.matrix_chat_enabled. Forced here because staging + prod share ONE Back4App
// app / set of cloud functions, and the deployed companyManager.js has diverged from git
// (wallet fix 50384efe lives on origin/map-editor, never merged to staging), so we can't
// safely ship the getCurrentUserCompany change that surfaces matrix_chat_enabled without
// regressing wallet billing. The Company record already has matrix_chat_enabled=true +
// matrix_chat_domain set, and the server /api/chat/token route reads those directly — only
// this client-side gate is missing the field.
const FORCE_MATRIX_CHAT_COMPANY_IDS = new Set<string>([
  'qLtBoL3huY', // Openprovider
])

const openChatPage = () => routingUtils.navigateToSection('chat')

function ChatPanelSlot({ displayName, inRoom }: Readonly<{ displayName?: string; inRoom: boolean }>) {
  const { currentCompany, loading } = useCompanyPermissions()
  const companyId = currentCompany?.id
  const companyOptedIn =
    currentCompany?.matrix_chat_enabled === true ||
    (companyId !== undefined && FORCE_MATRIX_CHAT_COMPANY_IDS.has(companyId))
  // Latch: a later `loading` flip must not unmount (or flip the backend of) a running chat client.
  const matrixOn = useLatchedValue(companyOptedIn, !loading)
  if (matrixOn === undefined) return null
  return <MatrixChatMount displayName={displayName} backend={matrixOn ? 'matrix' : 'parse'}
      inRoom={inRoom}
      onOpenChatPage={openChatPage}
    />
}

/**
 * This App component is kept for backward compatibility
 */
function App() {
  const [adminPanelVisible, setAdminPanelVisible] = useState(() => {
    const storedValue = localStorage.getItem('adminPanelVisible')
    return storedValue !== null ? storedValue === 'true' : true
  })
  const [playerManagementVisible, setPlayerManagementVisible] = useState(false)
  const [isOAuthCallback, setIsOAuthCallback] = useState(false)
  const oauthProcessingRef = useRef(false)
  const [isPasswordReset, setIsPasswordReset] = useState(() => {
    if (typeof window === 'undefined') return false
    return normalizeRoutePath(window.location.pathname) === '/reset-password'
  })
  const [resetToken, setResetToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    if (normalizeRoutePath(window.location.pathname) !== '/reset-password') return null
    return new URLSearchParams(window.location.search).get('token')
  })
  const [isEmailVerification, setIsEmailVerification] = useState(() => {
    if (typeof window === 'undefined') return false
    return normalizeRoutePath(window.location.pathname) === '/verify-email'
  })
  const [isAcceptInvitation, setIsAcceptInvitation] = useState(() => {
    if (typeof window === 'undefined') return false
    return normalizeRoutePath(window.location.pathname) === '/accept-invitation'
  })
  const [isJoinCompany, setIsJoinCompany] = useState(() => {
    if (typeof window === 'undefined') return false
    return normalizeRoutePath(window.location.pathname) === JOIN_COMPANY_PATH
  })
  const [isInvitationEmailPreview, setIsInvitationEmailPreview] = useState(() => {
    if (typeof window === 'undefined') return false
    return normalizeRoutePath(window.location.pathname) === '/invitation-email-preview'
  })
  const [isMeetingJoin, setIsMeetingJoin] = useState(() => {
    if (typeof window === 'undefined') return false
    return normalizeRoutePath(window.location.pathname) === '/meeting/join'
  })
  const [isMeetingFarewell] = useState(() => {
    if (typeof window === 'undefined') return false
    return normalizeRoutePath(window.location.pathname) === '/meeting/farewell'
  })
  // Capture meeting token once at init so it survives URL changes from the routing store
  const [meetingJoinToken] = useState(() => {
    if (typeof window === 'undefined') return null
    return new URLSearchParams(window.location.search).get('token')
  })
  const dispatch = useAppDispatch()
  const loggedIn = useAppSelector((state) => state.user.loggedIn)
  const authenticated = useAppSelector((state) => state.user.authenticated)
  // SC-438: first-run prompts wait while the onboarding wizard is checked or showing.
  const onboardingHoldsApp = useAppSelector(selectOnboardingHoldsApp)
  const computerDialogOpen = useAppSelector((state) => state.computer.computerDialogOpen)
  const roomJoined = useAppSelector((state) => state.room.roomJoined)
  // Display name for the Matrix chat panel (applied to the user's Matrix profile).
  const matrixChatDisplayName = useAppSelector((state) => {
    const u = state.user?.userData
    return u ? getDisplayName(u, '') || undefined : undefined
  })
  const userData = useAppSelector((state) => state.user.userData)
  const profileDialogOpen = useAppSelector((state) => state.user.profileDialogOpen)
  // The server-verified admin flag, not the role string: the Admin Panel mount and the menu's admin
  // items follow the same one (SC-439).
  const isAdmin = useElevatedAdmin()

  useAdminSessionCapabilities()

  const pathname =
    typeof window !== 'undefined' ? normalizeRoutePath(window.location.pathname) : ''
  const isProfileRoute = pathname === '/profile' || pathname === '/profile/edit'

  const isLoading = useSelector((state: RootState) => state.game.loadingGame)
  const [isInitializing, setIsInitializing] = useState(true)

  const handleAcceptInvitationComplete = useCallback(() => {
    setIsAcceptInvitation(false)
  }, [])

  // Add beforeunload warning when user tries to close the tab
  useBeforeUnload({
    enabled: roomJoined, // Only show warning when user is active in a room session
    message: 'Are you sure you want to leave Sevv? You may lose your current session.'
  })

  // Cleanup on actual page unload (NOT beforeunload — that fires when the
  // browser's "are you sure?" dialog appears, before the user confirms).
  // Running cleanup in beforeunload disconnects the session while the dialog
  // is still showing, so if the user clicks "Cancel" they're already offline.
  useEffect(() => {
    const handleUnload = () => {
      // Clean up pending room state so a stale room ID doesn't trigger auto-join on reload
      try {
        localStorage.removeItem('pendingRoomId')
        localStorage.removeItem('pendingRoomPassword')
        localStorage.removeItem('roomHasPassword')
      } catch { /* quota errors in private mode */ }

      if (loggedIn && window.networkInstance) {
        console.log('App: Cleaning up network on confirmed page unload')
        try {
          window.networkInstance.cleanup()
        } catch (error) {
          console.warn('App: Error during final cleanup:', error)
        }
      }
    }

    window.addEventListener('unload', handleUnload)

    return () => {
      window.removeEventListener('unload', handleUnload)
    }
  }, [loggedIn])

  // ENABLE NAMEPLATES - DISABLE CAMERA REPOSITIONING
  useEffect(() => {
    // Re-enable nameplates with 60 FPS updates
    if (typeof window !== 'undefined') {
      (window as any).NameplateManagerGlobalLoopActive = false; // Enable nameplates
      (window as any).disableProximityCalculations = false; // Enable proximity calculations
      (window as any).disableWebRTCProximity = false; // Enable WebRTC proximity
      (window as any).disableFollowAnimations = true; // Keep follow animations disabled
      (window as any).disableAreaDetection = false; // Enable area detection
    }

    // Disable camera repositioning/snapping
    disableCameraRepositioning();
    console.log('🎥 CAMERA FIX: Nameplates enabled, camera repositioning disabled');
  }, [])

  // Check for OAuth callback or password reset
  // This must run BEFORE routing initialization to prevent routing from intercepting OAuth callbacks
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    const error = urlParams.get('error')
    const token = urlParams.get('token')
    const pathname = normalizeRoutePath(window.location.pathname)

    // Check for OAuth callback routes - handle these BEFORE routing processes them
    if (pathname === '/auth/google/callback' || (pathname.startsWith('/auth/') && pathname.includes('/callback'))) {
      console.log('[App] OAuth callback route detected:', pathname)
      if (code || error) {
        setIsOAuthCallback(true)
        return
      }
    }

    // Check for password reset
    // Allow /reset-password route through - PasswordResetPage will handle token validation and errors
    if (pathname === '/reset-password') {
      console.log('[App] Password reset route detected:', pathname, token ? 'with token' : 'without token')
      setIsPasswordReset(true)
      setResetToken(token || null) // Set token or null - component will handle validation
      return
    }

    // Check for email verification
    if (pathname === '/verify-email') {
      console.log('[App] Email verification route detected:', pathname)
      setIsEmailVerification(true)
      return
    }

    // Non-redeemable invitation link explainer (template preview / test mail)
    if (pathname === '/invitation-email-preview') {
      console.log('[App] Invitation email placeholder route detected:', pathname)
      setIsInvitationEmailPreview(true)
      return
    }

    // Workspace invite link (SC-438), outside routing like the invitation magic link
    if (pathname === JOIN_COMPANY_PATH) {
      setIsJoinCompany(true)
      return
    }

    // Company invitation magic link (must bypass routing like password reset)
    if (pathname === '/accept-invitation') {
      console.log('[App] Accept invitation route detected:', pathname)
      setIsAcceptInvitation(true)
      return
    }

    // Check for OAuth callback (legacy check for other OAuth providers)
    if (code || error) {
      setIsOAuthCallback(true)
    }
  }, [])

  // Handle OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async () => {
      if (!isOAuthCallback) {
        oauthProcessingRef.current = false
        return
      }
      if (oauthProcessingRef.current) return
      oauthProcessingRef.current = true

      try {
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        const error = urlParams.get('error')
        const state = urlParams.get('state')
        const errorDescription = urlParams.get('error_description')

        // Handle OAuth errors from Google
        if (error) {
          console.error('[App] Google OAuth error:', error, errorDescription)

          let errorMessage: string
          if (error === 'access_denied') {
            errorMessage = AuthMessages.OAUTH_ACCESS_DENIED
          } else if (errorDescription && errorDescription.length < 100) {
            errorMessage = errorDescription
          } else {
            errorMessage = AuthMessages.OAUTH_GENERIC
          }

          sessionStorage.setItem('oauth_error', JSON.stringify({
            message: errorMessage,
            field: 'google',
            code: error,
            timestamp: Date.now()
          }))

          clearGoogleOAuthHandoffStorage()
          window.history.replaceState({}, document.title, window.location.pathname)
          setIsOAuthCallback(false)
          return
        }

        if (!code) {
          console.error('[App] No authorization code received')
          sessionStorage.setItem('oauth_error', JSON.stringify({
            message: AuthMessages.OAUTH_NO_CODE,
            field: 'google',
            timestamp: Date.now()
          }))
          clearGoogleOAuthHandoffStorage()
          window.history.replaceState({}, document.title, window.location.pathname)
          setIsOAuthCallback(false)
          return
        }

        const sessionState = sessionStorage.getItem('google_oauth_state')
        const localState = localStorage.getItem(GOOGLE_OAUTH_LOCAL_STATE_KEY)
        const stateCheck = validateGoogleOAuthReturnState(state, sessionState, localState)
        if (stateCheck.valid === false) {
          console.error('[App] OAuth state validation failed:', stateCheck.reason)
          sessionStorage.setItem('oauth_error', JSON.stringify({
            message: AuthMessages.OAUTH_CSRF,
            field: 'google',
            timestamp: Date.now()
          }))
          clearGoogleOAuthHandoffStorage()
          window.history.replaceState({}, document.title, window.location.pathname)
          setIsOAuthCallback(false)
          return
        }

        const sessionRedirect = sessionStorage.getItem('google_oauth_redirect_uri')
        const localRedirect = localStorage.getItem(GOOGLE_OAUTH_LOCAL_REDIRECT_KEY)
        const storedRedirectUri = sessionRedirect?.trim() || localRedirect?.trim() || null
        const redirectUri = resolveGoogleOAuthRedirectUri(
          window.location.pathname,
          window.location.origin,
          storedRedirectUri
        )
        if (!redirectUri) {
          console.error('[App] No redirect URI resolvable for OAuth callback')
          sessionStorage.setItem('oauth_error', JSON.stringify({
            message: AuthMessages.OAUTH_NO_REDIRECT_URI,
            field: 'google',
            timestamp: Date.now()
          }))
          clearGoogleOAuthHandoffStorage()
          window.history.replaceState({}, document.title, window.location.pathname)
          setIsOAuthCallback(false)
          return
        }

        console.log('[App] Handling OAuth callback with code:', code.substring(0, 10) + '...')

        const oauthIntent = sessionStorage.getItem('google_oauth_intent')
        if (oauthIntent === 'link') {
          sessionStorage.removeItem('google_oauth_intent')
          try {
            await authService.linkGoogleAccountWithCode(code, redirectUri)
            const cu = Parse.User.current()
            if (cu) {
              await cu.fetch()
            }
            const prev = store.getState().user.userData
            if (prev) {
              dispatch(
                setUserData({
                  ...prev,
                  googleLinked: true,
                  emailVerified: true,
                })
              )
            }
            authService.clearInMemoryProfileCache()
            clearGoogleOAuthHandoffStorage()
            sessionStorage.removeItem('oauth_error')
            window.history.replaceState({}, document.title, '/')
            setIsOAuthCallback(false)
            return
          } catch (linkErr: any) {
            const msg =
              linkErr?.message ||
              'Could not link Google. Ensure your Google email matches your account email.'
            sessionStorage.setItem(
              'oauth_error',
              JSON.stringify({
                message: msg,
                field: 'google',
                timestamp: Date.now(),
              })
            )
            clearGoogleOAuthHandoffStorage()
            window.history.replaceState({}, document.title, window.location.pathname)
            setIsOAuthCallback(false)
            return
          }
        }

        // Handle the OAuth callback (sign-in / sign-up). The sign-up choice is only present
        // when this flow (matched by its validated state) started on Create Account; the clear
        // on every exit below removes it.
        const authResponse = await authService.handleGoogleOAuthCallbackWithStoredConsent(
          code,
          redirectUri,
          state
        )

        // Verify authentication was successful
        if (!authResponse.user || !authResponse.user.id) {
          throw new Error('Authentication failed - no user data received')
        }

        // Map to Redux store format following Back4App best practices
        // Google OAuth users have emailVerified = true (Google pre-verifies emails)
        const emailVerified = authResponse.user.emailVerified !== undefined
          ? authResponse.user.emailVerified
          : true // Default to true for Google OAuth users

        const profile = coerceUserProfile(authResponse.profile)
        const userData = userProfileToFullUserData(
          {
            id: authResponse.user.id,
            email: authResponse.user.email,
            username: authResponse.user.username,
            role: (profile.role as UserRole) || 'user',
            createdAt: authResponse.user.createdAt
              ? typeof authResponse.user.createdAt === 'string'
                ? authResponse.user.createdAt
                : new Date(authResponse.user.createdAt).toISOString()
              : new Date().toISOString(),
            emailVerified,
          },
          profile,
          readParseGoogleLinked()
        )

        dispatch(setUserData(userData))

        // Update player name mapping for immediate UI updates (only if in a room session)
        const displayName = getDisplayName(userData)
        const sessionId = store.getState().user.sessionId
        if (sessionId) {
          dispatch(setPlayerName({ id: sessionId, name: displayName }))
        }

        // Player name service removed - using centralized nameplate system instead

        dispatch(setAuthenticated(true))

        if (authResponse.isNewUser) {
          dispatch(
            addNotification({
              type: 'success',
              message: AuthMessages.OAUTH_ACCOUNT_CREATED,
              duration: 6000,
            })
          )
          dispatch(
            addNotification({
              type: 'info',
              message: AuthMessages.OAUTH_PASSWORD_HINT,
              duration: 8000,
            })
          )
        }

        clearGoogleOAuthHandoffStorage()
        sessionStorage.removeItem('oauth_error')
        sessionStorage.removeItem('oauth_domain_error')
        window.history.replaceState({}, document.title, '/')

        console.log('[App] Google OAuth authentication successful')
        setIsOAuthCallback(false)

      } catch (err: any) {
        console.error('[App] OAuth callback error:', err)

        // Left for the sign-in dialog after the redirect: closed sign-up (popup), a domain
        // refusal (under the email field) or anything else (toast).
        const { key, value } = googleCallbackErrorEntry(err, Date.now())
        sessionStorage.setItem(key, JSON.stringify(value))

        clearGoogleOAuthHandoffStorage()
        window.history.replaceState({}, document.title, '/')
        setIsOAuthCallback(false)
      }
    }

    handleOAuthCallback()
  }, [isOAuthCallback, dispatch])

  // Session checking and workspace settings loading are now handled by AppController
  useEffect(() => {
    // Set initialization to false after a brief delay to allow AppController to handle initialization
    const timer = setTimeout(() => {
      setIsInitializing(false)
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  // SC-323: Guest auto-join — once the guest page completes and Phaser is ready,
  // trigger room join via the initialization pipeline.
  const guestMeetingToken = useAppSelector((state) => (state as any).guest?.meetingToken)
  const guestMeetingInfo = useAppSelector((state) => (state as any).guest?.meetingInfo)
  const guestContext = useAppSelector((state) => (state as any).guest?.guestContext)
  const guestAutoJoinTriggered = useRef(false)

  // SC-371: Escort panel — rendered once here (not inside GameMenuController which mounts twice)
  const { guestGroup: escortGuestGroup, dismiss: dismissEscortPanel } = useReceptionGuests()

  // SC-371: Re-fetch myTodaysBookings whenever a booking is created/updated/cancelled mid-session.
  // bookingsRefreshTrigger is incremented by BookingFormDialog and Colyseus message handlers.
  const bookingsRefreshTrigger = useAppSelector((s) => s.roomBooking.bookingsRefreshTrigger)
  const companyId = useAppSelector((s) => s.room.companyId || s.user.userData?.companyId)
  // Only the game room uses today's bookings, and joining one reads them (prefetchTodaysBookings),
  // so a booking change on the dashboard skips this read (SC-477). A ref, not a dependency:
  // joining must not read them a second time.
  const roomJoinedRef = useRef(roomJoined)
  roomJoinedRef.current = roomJoined
  useEffect(() => {
    console.log('[App] bookingsRefreshTrigger changed:', bookingsRefreshTrigger, 'companyId:', companyId)
    if (!companyId || bookingsRefreshTrigger === 0 || !roomJoinedRef.current) return
    const now = new Date()
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
    const dayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString()
    console.log('[App] Refetching myTodaysBookings...')
    // parseCloudRun lets a company-closed refusal (SC-409) re-check access. No sign-out on an
    // expired token: this refresh is background work, so session handling stays as it was.
    parseCloudRun<any>(
      'getRoomBookings',
      {
        companyId: companyId,
        creatorOrInvitee: true,
        dateFilter: 'today',
        dayStart,
        dayEnd,
      },
      { handleInvalidSession: false }
    ).then((res: any) => {
      console.log('[App] myTodaysBookings refetched:', res?.bookings?.length, 'bookings')
      dispatch(setMyTodaysBookings(res?.bookings || []))
    }).catch((err: any) => { console.error('[App] myTodaysBookings refetch failed:', err) })
  }, [bookingsRefreshTrigger, companyId, dispatch])

  useEffect(() => {
    if (!guestMeetingToken || !guestMeetingInfo || guestAutoJoinTriggered.current || isMeetingJoin) return

    let cancelled = false

    const initAndJoin = async () => {
      // Phaser is lazily loaded — guests don't go through MainMenu, so we must init it here
      console.log('[App] Guest auto-join: initializing Phaser...')
      const phaserGame = await initializePhaserGame()

      // Wait for bootstrap scene to finish preloading
      const waitForBootstrap = () => new Promise<any>((resolve) => {
        const check = () => {
          const bootstrap = phaserGame.scene?.keys?.bootstrap as any
          if (bootstrap?.preloadComplete) return resolve(bootstrap)
          setTimeout(check, 300)
        }
        check()
      })

      const bootstrap = await waitForBootstrap()
      if (cancelled || guestAutoJoinTriggered.current) return

      guestAutoJoinTriggered.current = true

      const customRoomId = guestMeetingInfo.customRoomId || 'default'
      console.log('[App] Guest auto-join: triggering room join', { customRoomId, token: guestMeetingToken.slice(0, 10) + '...' })

      // SC-323: Safety timeout — force-dismiss loading screen if something hangs
      // during game initialization (Parse queries, virtual room areas, etc.)
      const safetyTimer = setTimeout(() => {
        if (store.getState().game.loadingGame) {
          console.warn('[App] Guest safety timeout: force-dismissing loading screen after 8s')
          store.dispatch(setLoadingGame(false))
        }
      }, 8000)

      const result = await bootstrap.joinRoomWithPipeline({
        roomId: customRoomId,
        customRoomId,
        companyId: guestMeetingInfo.companyId,
        origin: 'guest_meeting' as any,
        meetingToken: guestMeetingToken,
        guestName: guestContext?.guestName,
        guestCompany: guestContext?.guestCompany,
        guestAvatar: guestContext?.guestAvatar,
      })

      clearTimeout(safetyTimer)

      if (!result.success) {
        console.error('[App] Guest auto-join failed:', result.error)
        // Nothing renders behind a failed join, so always leave for a screen that explains it
        window.location.href = guestJoinFailureUrl(result.error, guestMeetingInfo, guestMeetingToken)
        return
      } else {
        // Ensure loading screen is dismissed after successful join
        store.dispatch(setLoadingGame(false))
      }
    }

    initAndJoin().catch((err) => {
      console.error('[App] Guest auto-join error:', err)
      window.location.href = guestJoinFailureUrl(err?.message, guestMeetingInfo, guestMeetingToken)
    })

    return () => { cancelled = true }
  }, [guestMeetingToken, guestMeetingInfo, guestContext, isMeetingJoin])

  // Helper to remove preloader
  const removePreloader = () => {
    const preloader = document.getElementById('initial-preloader')
    if (preloader && preloader.style.opacity !== '0') {
      preloader.style.transition = 'opacity 0.5s ease'
      preloader.style.opacity = '0'
      setTimeout(() => {
        preloader.remove()
      }, 500)
    }
  }

  // Remove preloader when user is logged in and game is loaded
  // Or if user is logged in but NOT doing a game flow (e.g. dashboard)
  useEffect(() => {
    // If we are logged in:
    // 1. If we are NOT in a room, we are in dashboard -> Remove preloader immediately.
    // 2. If we ARE in a room, we must wait until isLoading is false (game done loading).

    if (loggedIn) {
      if (!roomJoined) {
        // Not in a room, safe to show dashboard
        // Add a small delay to ensure dashboard components are mounted and background is rendered
        setTimeout(() => removePreloader(), 200)
      } else if (!isLoading) {
        // In a room, and NOT loading. 
        // We ensure OptimizedLoadingScreen or Game has mounted.
        setTimeout(() => removePreloader(), 200)
      }
    }
  }, [loggedIn, isLoading, roomJoined])

  // Drop the HTML preloader immediately on dedicated auth/invite routes (before paint when possible)
  useLayoutEffect(() => {
    if (
      isPasswordReset ||
      isEmailVerification ||
      isOAuthCallback ||
      isAcceptInvitation ||
      isJoinCompany ||
      isInvitationEmailPreview ||
      isMeetingJoin ||
      isMeetingFarewell
    ) {
      removePreloader()
    }
  }, [
    isPasswordReset,
    isEmailVerification,
    isOAuthCallback,
    isAcceptInvitation,
    isJoinCompany,
    isInvitationEmailPreview,
    isMeetingJoin,
    isMeetingFarewell,
  ])

  // Initialize routing system
  useEffect(() => {
    setupRoutingListeners()
    initializeRouting()
  }, [])

  // Hide Phaser container when not in room (dashboard) to prevent room visible behind MainMenu
  useEffect(() => {
    const el = document.getElementById('phaser-container')
    if (el) el.style.visibility = roomJoined ? 'visible' : 'hidden'
  }, [roomJoined])

  // Removed legacy chat notification handling - now using centralized notification system

  const toggleAdminPanel = () => {
    setAdminPanelVisible((prev) => {
      const newValue = !prev
      localStorage.setItem('adminPanelVisible', String(newValue))
      return newValue
    })
  }

  const togglePlayerManagement = () => {
    setPlayerManagementVisible((prev) => !prev)
  }

  useEffect(() => {
    // Initialize keyboard handler when room is joined
    if (roomJoined && !isLoading && authenticated) {
      keyboardHandler.initialize()
    }
  }, [roomJoined, isLoading, authenticated])

  useEffect(() => {
    // Initialize game when room is joined and loading is complete
    if (roomJoined && !isLoading && authenticated) {
      console.log('Room joined and loading complete, initializing game')

      // Ensure the URL reflects in-room state and not menu/dashboard
      const neutralPath = '/room'
      if (typeof window !== 'undefined' && window.location.pathname !== neutralPath) {
        window.history.replaceState({}, '', neutralPath)
      }

      // If we have a Game scene running, let's make sure it's updated with the current user data
      const phaserGame = getPhaserGameSync()
      if (phaserGame && isPhaserGameReady() && phaserGame.scene?.keys?.game && (phaserGame.scene.keys.game as any).scene?.isActive() && userData) {
        const game = phaserGame.scene.keys.game as any

        // Check if connected using the fact that roomJoined is true
        if (game.network && roomJoined && game.myPlayer) {
          // Player is already properly initialized during room join with complete data
          // Just ensure connection is ready
          if (!game.myPlayer.readyToConnect) {
            console.log('Setting player ready to connect')
            game.setMyPlayerReadyToConnect()
            dispatch(setLoggedIn(true))
          }
        }
      }
    }
  }, [roomJoined, isLoading, authenticated, userData, dispatch])

  // If we leave the room, allow routing store to restore menu URL on next navigation
  useEffect(() => {
    if (!roomJoined && !isLoading) {
      // No-op: routingStore will handle URL when navigating menus again
    }
  }, [roomJoined, isLoading])

  // Initialize UI with defensive checks
  let ui: JSX.Element | null = null

  // Whiteboard is now rendered as an overlay inside LiveKitVideoDisplayController,
  // so the game UI stays alive (SC-342).
  if (loggedIn) {
    ui = (
      <>
        <Suspense fallback={null}>
          <GameMenuController
            renderRoomOverlays={false}
            adminPanelVisible={adminPanelVisible}
            onToggleAdminPanel={toggleAdminPanel}
            onTogglePlayerManagement={togglePlayerManagement}
            playerManagementVisible={playerManagementVisible}
          />
        </Suspense>
        {shouldMountPreLoadAdminController({ verifiedAdmin: isAdmin }) && (
          <Suspense fallback={null}>
            <AdminController
              adminPanelVisible={adminPanelVisible}
              onToggleAdminPanel={toggleAdminPanel}
            />
          </Suspense>
        )}
        <HelperController />
        <RequiredProfileCheck />
      </>
    )
  } else {
    // Use the new modular AppController for authentication and room selection
    ui = <AppController />
  }

  // Ensure ui is never null - provide fallback
  if (!ui) {
    ui = <AppController />
  }

  // Add a useEffect to handle player initialization separately from render
  useEffect(() => {
    // Only run this effect when relevant state changes
    if (roomJoined && authenticated && !loggedIn && !isLoading && userData) {
      const phaserGame = getPhaserGameSync()
      if (!phaserGame || !isPhaserGameReady()) return

      const game = phaserGame.scene?.keys?.game as any

      if (game && game.scene?.isActive() && game.myPlayer) {
        const displayName = getDisplayName(userData, 'Guest')

        // Set player data
        game.myPlayer.setPlayerName(displayName)
        game.myPlayer.setPlayerRole(userData?.role || 'user')
        game.myPlayer.setPlayerTexture(userData?.gameCharacter || 'adam')

        // Initialize player connection status
        console.log('Setting up player from App.tsx effect')

        // Ensure readyToConnect and connectionStatus are properly set
        if (game.network) {
          // First set to LOADING state
          dispatch(setConnectionStatus(ConnectionStatus.LOADING))

          // Wait briefly to ensure server state is updated
          setTimeout(() => {
            // Then finalize to ONLINE state
            console.log('App: Finalizing player connection after brief delay')
            game.network.finalizeConnection()

            // Trigger LiveKit auto-connection after player connection is finalized
            window.dispatchEvent(
              new CustomEvent(LIVEKIT_AUTO_CONNECT_AFTER_LOADING, { detail: { source: 'AppPlayerFinalization' } })
            )

            dispatch(setLoggedIn(true))
          }, 500)
        } else {
          dispatch(setLoggedIn(true))
        }
      }
    }
  }, [roomJoined, authenticated, loggedIn, isLoading, userData, dispatch])

  return (
    <AppErrorBoundary>
        <CssBaseline />
        <GlobalStyle />
        <Backdrop>
          <LiveKitCallControlsProvider>
          {isMeetingFarewell ? (
            <GuestFarewellScreen />
          ) : isMeetingJoin ? (
            <GuestJoinPage
              token={meetingJoinToken}
              onComplete={() => setIsMeetingJoin(false)}
            />
          ) : isInvitationEmailPreview ? (
            <InvitationEmailPlaceholderPage />
          ) : isAcceptInvitation ? (
            <AcceptInvitationPage onComplete={handleAcceptInvitationComplete} />
          ) : isJoinCompany ? (
            <JoinCompanyPage onComplete={() => setIsJoinCompany(false)} />
          ) : isPasswordReset ? (
            <PasswordResetPage
              token={resetToken}
              onSuccess={() => {
                setIsPasswordReset(false)
                setResetToken(null)
                window.history.replaceState({}, document.title, '/')
              }}
            />
          ) : isEmailVerification ? (
            <EmailVerificationPage
              onSuccess={() => {
                setIsEmailVerification(false)
                window.history.replaceState({}, document.title, '/')
              }}
            />
          ) : isOAuthCallback ? (
            <GoogleOAuthCallback />
          ) : isInitializing ? (
            null
          ) : (
            <>
              {/* SC-360: until a signed-in user has accepted the current Terms, AppController
                  shows the Terms screen instead of the menu, so room selection, Phaser and the
                  rid/room deep links (all inside MainMenu) cannot start, and the siblings below
                  wait too. The provider never swaps its children, so AppController stays
                  mounted through the check. Guests never see it. */}
              <TermsGateProvider
                enabled={authenticated && !guestContext && !isGuestUserId(userData?.id)}
                userId={userData?.id ?? null}
              >
                {!isLoading && ui}

                <TermsGated>
                  {authenticated && !onboardingHoldsApp && <RequiredProfileCheck />}
                  {authenticated && !onboardingHoldsApp && <FeedbackDrawer />}
                  {authenticated && !onboardingHoldsApp && <ProductUpdatesDialogHost />}
                  {/* SC-473: single chat instance — popup in-game (everyone), docked to the Chat page on the dashboard (signed-in, non-guest).
                      SC-438: not behind the onboarding wizard, which may run before the person has a company. */}
                  {!onboardingHoldsApp &&
                    shouldMountChatPanel({
                      authenticated,
                      isGuest: Boolean(guestContext) || isGuestUserId(userData?.id),
                      roomJoined,
                      isLoading,
                    }) && (
                    <Suspense fallback={null}>
                      <ChatPanelSlot displayName={matrixChatDisplayName} inRoom={roomJoined} />
                    </Suspense>
                  )}
                </TermsGated>
              </TermsGateProvider>
              {/* SC-247: mount the in-room bottom bar on `roomJoined` only — NOT `!isLoading`.
                  On slow connections `loadingGame` stays true (final load-pipeline step) long after
                  the room is joined, which kept the mic/video control bar (and its connecting
                  placeholder) hidden for minutes. Mounting at `roomJoined` lets the lazy
                  GameMenuController chunk download during the loading phase so the bar is ready the
                  moment the loading screen clears. Wrapped in SafeSubtree: if the toolbar renders
                  before the game scene is ready and throws, it fails to nothing instead of crashing
                  the app, and auto-retries when loading completes (`isLoading` flips). */}
              {shouldMountInRoomBottomBar({ roomJoined }) && (
                <SafeSubtree label="in-room-bottom-bar" resetKeys={[isLoading]}>
                  <Suspense fallback={null}>
                    <GameMenuController
                      onToggleAdminPanel={isAdmin ? toggleAdminPanel : undefined}
                      adminPanelVisible={adminPanelVisible}
                      onTogglePlayerManagement={togglePlayerManagement}
                      playerManagementVisible={playerManagementVisible}
                    />
                  </Suspense>
                </SafeSubtree>
              )}

              {/* SC-371: Escort guest panel — rendered once here, outside GameMenuController
                  (which mounts twice) to avoid duplicate fixed-position panels. */}
              {roomJoined && !guestContext && escortGuestGroup && (
                <EscortGuestPanel
                  guests={escortGuestGroup.guests}
                  bookingId={escortGuestGroup.bookingId}
                  onDismiss={dismissEscortPanel}
                />
              )}

              {/* Helper stays gated on full load completion; the chat panel is mounted above (SC-473). */}
              {!isLoading && roomJoined && (
                <>
                  <HelperController />
                  {/* NameplateList removed — now rendered inside phaser-react-ui overlay */}
                </>
              )}
              {shouldMountInRoomAdminController({
                verifiedAdmin: isAdmin,
                roomJoined,
                isLoading,
              }) && (
                <>
                  <Suspense fallback={null}>
                    <AdminController
                      adminPanelVisible={adminPanelVisible}
                      onToggleAdminPanel={toggleAdminPanel}
                    />
                  </Suspense>
                </>
              )}
              {!isLoading && isAdmin && roomJoined && (
                <></>
              )}
              {computerDialogOpen && (
                <Suspense fallback={null}>
                  <ComputerDialog />
                </Suspense>
              )}
              <Suspense fallback={null}>
                <LiveKitRoomConnector />
              </Suspense>
            </>
          )}
          {/* Mount HelpTooltip regardless of loading; it self-gates by connection/room */}
          <HelpTooltip />
          {/* SC-255: the one pinned column every top-centre banner portals into, so they
              share a coordinate space and cannot overlap or mismatch in size. */}
          <StatusBannerViewport />
          <ConnectionUnstableToast />
          <Notifications />
          <RichNotificationToast />
          {/* SC-255: self-gates on `roomJoined` — in-game only, never on the dashboard. */}
          <ZoomedControlsIndicator />
          {isLoading && !isPasswordReset && !isEmailVerification && !isOAuthCallback && !isAcceptInvitation && !isJoinCompany && !isInvitationEmailPreview && !isMeetingJoin && !isMeetingFarewell && !profileDialogOpen && !isProfileRoute && <OptimizedLoadingScreen />}
          </LiveKitCallControlsProvider>
        </Backdrop>
    </AppErrorBoundary>
  )
}

export default App
